import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const instances = new Set();
const drafts = new Map();
const productDrafts = new Map();
const keys = ['Edition', 'Language', 'Second language', 'Name on box', 'Dedication message'];
const single = 'Single-language edition';
const bilingual = 'Bilingual family edition';
let saving = false;

const decodeLiquidProperty = (value) => {
  if (typeof value !== 'string' || !value.includes('&')) return value;
  const decoder = document.createElement('textarea');
  decoder.innerHTML = value;
  return decoder.value;
};
const propertiesObject = (value) => Array.isArray(value)
  ? Object.fromEntries(value.map(([key, entry]) => [decodeLiquidProperty(key), decodeLiquidProperty(entry)]))
  : (value && typeof value === 'object' ? value : {});
const signature = (value) => JSON.stringify(Object.entries(propertiesObject(value)).sort(([a], [b]) => a.localeCompare(b)));
const root = () => window.Shopify?.routes?.root || '/';
const cartInstances = () => [...instances].filter((instance) => instance.isConnected && instance.isCart);
const activeEditor = (predicate) => {
  const matches = cartInstances().filter(predicate);
  return matches.find((instance) => instance.getClientRects().length) || matches[0];
};

function lockSaving(value) {
  saving = value;
  for (const instance of cartInstances()) {
    const fieldset = instance.querySelector('fieldset');
    if (fieldset) fieldset.disabled = value;
    instance.setAttribute('aria-busy', String(value));
  }
  document.querySelectorAll('[name="checkout"], .cart__checkout-button').forEach((button) => {
    if (value && !button.disabled) {
      button.dataset.quesboSavingLock = 'true';
      button.disabled = true;
    } else if (!value && button.dataset.quesboSavingLock === 'true') {
      button.disabled = false;
      delete button.dataset.quesboSavingLock;
    }
  });
}

function prevent(event, editor, message) {
  event.preventDefault();
  event.stopImmediatePropagation();
  if (event.target instanceof HTMLInputElement && event.target.closest('.quantity-selector, quantity-selector-component, quantity-selector')) {
    event.target.value = event.target.defaultValue;
  }
  editor?.showStatus(message || editor.dataset.unsavedMessage, true, true);
}

function guard(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const form = target instanceof HTMLFormElement ? target : target.closest('form');
  if (event.type === 'submit' && form) {
    for (const instance of instances) {
      if (!instance.isCart && instance.closest('form') === form && !instance.validate(true)) {
        prevent(event, null);
        return;
      }
    }
  }
  const checkout = !!target.closest('[name="checkout"], .cart__checkout-button, a[href*="/checkout"], shopify-accelerated-checkout-cart');
  const cartSubmit = event.type === 'submit' && form?.matches('.cart-form, form[action$="/cart"]');
  const quantity = event.type === ThemeEvents.quantitySelectorUpdate || !!target.closest('cart-items-component .quantity-selector, cart-items-component quantity-selector-component, cart-items-component quantity-selector, .cart-items__remove, cart-items-component [name="updates[]"]');
  const adding = event.type === 'submit' && form?.action.includes('/cart/add');
  const dirty = activeEditor((instance) => drafts.has(instance.dataset.key));
  if ((checkout || cartSubmit || quantity || (adding && saving)) && (dirty || saving)) {
    const editor = dirty || activeEditor(() => true);
    prevent(event, editor, saving ? editor?.dataset.savingMessage : editor?.dataset.unsavedMessage);
    return;
  }
  if (checkout || cartSubmit) {
    const invalid = activeEditor((instance) => instance.stockCorrectionRequired || !instance.validate());
    if (invalid) {
      prevent(event, invalid, invalid.stockCorrectionRequired ? invalid.dataset.stockCorrectionMessage : invalid.dataset.unavailableMessage);
      invalid.validate(true);
    }
  }
}

for (const type of ['click', 'submit', 'change', ThemeEvents.quantitySelectorUpdate]) {
  window.addEventListener(type, guard, true);
}
window.addEventListener('beforeunload', (event) => {
  if (drafts.size || saving) {
    event.preventDefault();
    event.returnValue = '';
  }
});

class QuesboGameOptions extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.optionsController?.abort();
    this.optionsController = new AbortController();
    const { signal } = this.optionsController;
    instances.add(this);
    this.addEventListener('input', this.onInput, { signal });
    this.addEventListener('change', this.onInput, { signal });
    this.addEventListener('click', this.onClick, { signal });
    this.optionsObserver?.disconnect();
    this.optionsObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => !mutation.target.closest?.('[data-status]'))) this.scheduleSync();
    });
    this.optionsObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-key', 'data-properties', 'data-quantity', 'data-allowed-languages', 'data-in-stock', 'value', 'selected'],
    });
    this.scheduleSync();
  }

  disconnectedCallback() {
    instances.delete(this);
    this.optionsController?.abort();
    this.optionsObserver?.disconnect();
    this.saveController?.abort();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.scheduleSync();
  }

  get isCart() {
    return this.dataset.context === 'cart';
  }

  get isStock() {
    return this.dataset.inStock === 'true';
  }

  get stockCorrectionRequired() {
    if (!this.isCart || !this.isStock) return false;
    const properties = this.properties;
    const edition = String(properties.Edition || '').trim();
    return (!!edition && edition !== single) || ['Second language', 'Name on box', 'Dedication message'].some((key) => String(properties[key] || '').trim());
  }

  get properties() {
    try { return propertiesObject(JSON.parse(this.dataset.properties || '{}')); } catch { return {}; }
  }

  get languages() {
    try { return JSON.parse(this.dataset.allowedLanguages || '["English"]'); } catch { return ['English']; }
  }

  fields() {
    return [...this.querySelectorAll('[data-property]')];
  }

  baseline() {
    const properties = this.properties;
    return this.normalise({
      Edition: properties.Edition || single,
      Language: properties.Language || this.dataset.defaultLanguage || 'English',
      'Second language': properties['Second language'] || '',
      'Name on box': properties['Name on box'] || '',
      'Dedication message': properties['Dedication message'] || '',
    });
  }

  normalise(values) {
    const result = Object.fromEntries(keys.map((key) => [key, values[key] ?? '']));
    if (this.isStock) {
      result.Edition = single;
      result['Second language'] = '';
      result['Name on box'] = '';
      result['Dedication message'] = '';
    } else if (result.Edition !== bilingual) result['Second language'] = '';
    return result;
  }

  values() {
    return this.normalise(Object.fromEntries(this.fields().map((field) => [field.dataset.property, field.value])));
  }

  apply(values) {
    const normalised = this.normalise(values);
    for (const field of this.fields()) {
      const next = normalised[field.dataset.property] || '';
      if (field.value !== next) field.value = next;
    }
    this.validate();
  }

  reflectCorrection() {
    const save = this.querySelector('[data-save]');
    const label = this.stockCorrectionRequired ? this.dataset.stockConfirmLabel : this.dataset.saveLabel;
    if (save && label && save.textContent !== label) save.textContent = label;
  }

  scheduleSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;
    queueMicrotask(() => {
      this.syncQueued = false;
      if (!this.isConnected || !this.fields().length) return;
      if (this.isCart) {
        let draft = drafts.get(this.dataset.key);
        if (!draft) {
          const matching = [...drafts].find(([, value]) => value.variantId === this.dataset.variantId && value.original === signature(this.properties));
          if (matching) {
            drafts.delete(matching[0]);
            drafts.set(this.dataset.key, matching[1]);
            draft = matching[1];
          }
        }
        if (draft && this.isStock) {
          draft.values = this.normalise(draft.values);
          if (signature(draft.values) === signature(this.baseline())) {
            drafts.delete(this.dataset.key);
            draft = undefined;
          }
        }
        this.apply(draft?.values || this.baseline());
        this.dataset.dirty = String(!!draft);
        this.reflectCorrection();
        if (this.stockCorrectionRequired) this.showStatus(this.dataset.stockCorrectionMessage, true);
        else if (draft) this.showStatus(this.dataset.unsavedMessage);
        else if (!this.validate()) this.showStatus(this.dataset.unavailableMessage, true);
        const fieldset = this.querySelector('fieldset');
        if (fieldset) fieldset.disabled = saving;
      } else {
        this.apply(productDrafts.get(this.dataset.productId) || this.baseline());
        if (this.isStock) productDrafts.set(this.dataset.productId, this.values());
      }
    });
  }

  validate(report = false) {
    const fields = Object.fromEntries(this.fields().map((field) => [field.dataset.property, field]));
    if (!fields.Edition || !fields.Language || (!this.isStock && !fields['Second language'])) return false;
    if (this.isStock && fields.Edition.value !== single) fields.Edition.value = single;
    const isBilingual = !this.isStock && fields.Edition.value === bilingual;
    const bilingualOption = this.querySelector('[data-bilingual-option]');
    if (bilingualOption) bilingualOption.disabled = this.languages.length < 2;
    const bilingualNote = this.querySelector('[data-bilingual-note]');
    if (bilingualNote) bilingualNote.hidden = this.languages.length < 2 && !isBilingual;
    const secondField = this.querySelector('[data-second-field]');
    if (secondField) secondField.hidden = !isBilingual;
    if (fields['Second language']) {
      fields['Second language'].disabled = !isBilingual;
      fields['Second language'].required = isBilingual;
    }
    fields.Edition.setCustomValidity(![single, bilingual].includes(fields.Edition.value) || (isBilingual && this.languages.length < 2) ? this.dataset.unavailableMessage : '');
    fields.Language.setCustomValidity(this.languages.includes(fields.Language.value) ? '' : this.dataset.unavailableMessage);
    const second = fields['Second language'];
    let secondError = '';
    if (isBilingual && second?.value && !this.languages.includes(second.value)) secondError = this.dataset.unavailableMessage;
    else if (isBilingual && second?.value === fields.Language.value) secondError = this.dataset.sameLanguageMessage;
    second?.setCustomValidity(secondError);
    const invalid = this.fields().find((field) => !field.disabled && !field.checkValidity());
    if (invalid && report) {
      this.querySelector('details').open = true;
      invalid.reportValidity();
    }
    return !invalid;
  }

  onInput = (event) => {
    if (!event.target.matches('[data-property]')) return;
    this.validate();
    const values = this.values();
    if (!this.isCart) {
      productDrafts.set(this.dataset.productId, values);
      return;
    }
    const base = this.baseline();
    if (base.Edition !== bilingual) base['Second language'] = '';
    const changed = signature(values) !== signature(base);
    if (changed) drafts.set(this.dataset.key, { values, original: signature(this.properties), variantId: this.dataset.variantId });
    else drafts.delete(this.dataset.key);
    for (const instance of cartInstances().filter((instance) => instance.dataset.key === this.dataset.key)) {
      if (instance !== this) instance.apply(values);
      instance.dataset.dirty = String(changed);
      instance.reflectCorrection();
      instance.showStatus(instance.stockCorrectionRequired ? instance.dataset.stockCorrectionMessage : (changed ? instance.dataset.unsavedMessage : ''), instance.stockCorrectionRequired);
    }
  };

  onClick = (event) => {
    if (event.target.closest('[data-save]')) {
      event.preventDefault();
      this.save();
    } else if (event.target.closest('[data-cancel]')) {
      event.preventDefault();
      if (saving) return;
      drafts.delete(this.dataset.key);
      for (const instance of cartInstances().filter((instance) => instance.dataset.key === this.dataset.key)) {
        instance.apply(instance.baseline());
        instance.dataset.dirty = 'false';
        instance.reflectCorrection();
        instance.showStatus(instance.stockCorrectionRequired ? instance.dataset.stockCorrectionMessage : (instance.validate() ? '' : instance.dataset.unavailableMessage), instance.stockCorrectionRequired || !instance.validate());
      }
    }
  };

  showStatus(message, error = false, focus = false) {
    const status = this.querySelector('[data-status]');
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
    status.dataset.error = String(error);
    if (message) this.querySelector('details').open = true;
    if (focus) status.focus({ preventScroll: false });
  }

  async save() {
    if (saving || !this.isCart || !this.validate(true)) return;
    const oldKey = this.dataset.key;
    const original = signature(this.properties);
    const values = this.values();
    const source = this.closest('cart-items-component');
    const sourceId = source?.dataset.sectionId;
    const requestController = new AbortController();
    this.saveController = requestController;
    lockSaving(true);
    this.showStatus(this.dataset.savingMessage);
    let committed = false;
    try {
      const currentResponse = await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: requestController.signal });
      if (!currentResponse.ok) throw new Error(this.dataset.errorMessage);
      const current = await currentResponse.json();
      const line = current.items.find((item) => item.key === oldKey) || current.items.find((item) => String(item.variant_id) === this.dataset.variantId && signature(item.properties) === original);
      if (!line) throw new Error(this.dataset.missingMessage);
      const properties = { ...propertiesObject(line.properties) };
      for (const key of keys) delete properties[key];
      for (const [key, value] of Object.entries(values)) {
        if (String(value).trim()) properties[key] = String(value).trim();
      }
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        signal: requestController.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties, sections, sections_url: location.pathname }),
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || !Array.isArray(cart.items)) throw new Error(this.dataset.errorMessage);
      committed = true;
      drafts.delete(oldKey);
      const saved = cart.items.find((item) => String(item.variant_id) === this.dataset.variantId && signature(item.properties) === signature(properties));
      if (saved) {
        for (const instance of cartInstances().filter((instance) => instance.dataset.key === oldKey)) {
          instance.dataset.key = saved.key;
          instance.dataset.quantity = saved.quantity;
          instance.dataset.properties = JSON.stringify(saved.properties);
          instance.dataset.dirty = 'false';
          instance.reflectCorrection();
        }
      }
      const event = new CartUpdateEvent(cart, sourceId || this.id, { source: 'quesbo-game-options', itemCount: cart.item_count, sections: cart.sections });
      if (source?.isConnected) source.dispatchEvent(event);
      else document.dispatchEvent(event);
      if (sourceId) {
        if (cart.sections?.[sourceId]) await morphSection(sourceId, cart.sections[sourceId]);
        else await sectionRenderer.renderSection(sourceId, { cache: false });
      }
      await Promise.resolve();
      const editor = activeEditor((instance) => instance.dataset.key === saved?.key) || (this.isConnected ? this : null);
      if (editor) editor.showStatus(drafts.has(editor.dataset.key) ? editor.dataset.unsavedMessage : editor.dataset.savedMessage, false, true);
    } catch (error) {
      this.showStatus(committed ? this.dataset.savedMessage : (error.message || this.dataset.errorMessage), !committed, true);
    } finally {
      if (this.saveController === requestController) this.saveController = null;
      lockSaving(false);
    }
  }
}

if (!customElements.get('quesbo-game-options')) customElements.define('quesbo-game-options', QuesboGameOptions);
